import { useState, useCallback } from 'react'
import { startIngestionRun, verifyIngestionRun, flagIngestionCorrupt } from '../services/ingestion'
import type { IngestionRun } from '../types/ingestion'
import { useInvalidate } from './use-list-invalidation'

export interface FlagCorruptInput {
  caseId: string
  runId: string
  reason: string
}

interface UseIngestionMutationsResult {
  starting: boolean
  verifying: boolean
  flagging: boolean
  error: string | null
  startRun: (caseId: string, receiptId: string) => Promise<IngestionRun | null>
  verifyRun: (caseId: string, runId: string) => Promise<IngestionRun | null>
  flagCorrupt: (input: FlagCorruptInput) => Promise<IngestionRun | null>
  clearError: () => void
}

export function useIngestionMutations(): UseIngestionMutationsResult {
  const [starting, setStarting] = useState(false)
  const [verifying, setVerifying] = useState(false)
  const [flagging, setFlagging] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const invalidate = useInvalidate()

  const startRun = useCallback(
    async (caseId: string, receiptId: string) => {
      setStarting(true)
      setError(null)
      try {
        const run = await startIngestionRun(caseId, { collectionReceiptId: receiptId })
        invalidate(['ingestion_runs', 'case_activity'])
        return run
      } catch (error_) {
        setError(error_ instanceof Error ? error_.message : 'Failed to start ingestion run')
        return null
      } finally {
        setStarting(false)
      }
    },
    [invalidate]
  )

  const verifyRun = useCallback(
    async (caseId: string, runId: string) => {
      setVerifying(true)
      setError(null)
      try {
        const run = await verifyIngestionRun(caseId, runId)
        invalidate(['ingestion_runs', 'case_activity'])
        return run
      } catch (error_) {
        setError(error_ instanceof Error ? error_.message : 'Failed to verify ingestion run')
        return null
      } finally {
        setVerifying(false)
      }
    },
    [invalidate]
  )

  const flagCorrupt = useCallback(
    async (input: FlagCorruptInput) => {
      setFlagging(true)
      setError(null)
      try {
        const run = await flagIngestionCorrupt(input)
        invalidate(['ingestion_runs', 'case_activity'])
        return run
      } catch (error_) {
        setError(error_ instanceof Error ? error_.message : 'Failed to flag run as corrupt')
        return null
      } finally {
        setFlagging(false)
      }
    },
    [invalidate]
  )

  const clearError = useCallback(() => {
    setError(null)
  }, [])

  return { starting, verifying, flagging, error, startRun, verifyRun, flagCorrupt, clearError }
}
